import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Trash2, Users, Search, Mail, RefreshCw, Building2, Baby, Activity, MessageSquare, MapPin, Printer, FileText, Download, UserPlus, Lock, ExternalLink, Copy, TrendingUp, Send, RotateCcw, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { WebhookActivityTab } from "@/components/security-settings/WebhookActivityTab";
import FeedbackSuggestionsTab from "@/components/feedback/FeedbackSuggestionsTab";
import FeedbackResponsesTab from "@/components/feedback/FeedbackResponsesTab";
import IPLocationsTab from "@/components/admin/IPLocationsTab";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY_OWNER_EMAIL = "todayskidz@comcast.net";

const AdminCustomersPage = () => {
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const isPrimaryOwner = user?.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase();
  
  // Organizations state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  
  // Emails state
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("organizations");
  // SMS Test Limits admin tab state
  const [smsTestUsage, setSmsTestUsage] = useState({ month: "", rows: [] });
  const [smsTestUsageLoading, setSmsTestUsageLoading] = useState(false);
  const [resettingOrgId, setResettingOrgId] = useState(null);
  // Pickup Safety Usage admin tab state (Stage 7.6)
  const [pickupSafetyUsage, setPickupSafetyUsage] = useState({ month: "", rows: [] });
  const [pickupSafetyUsageLoading, setPickupSafetyUsageLoading] = useState(false);
  const [sendingUpgradeEmailFor, setSendingUpgradeEmailFor] = useState(null);
  // SMS Usage & Limits admin tab state (Stage 8 — parent-comm cap)
  const [smsAdminUsage, setSmsAdminUsage] = useState({ month: "", rows: [] });
  const [smsAdminUsageLoading, setSmsAdminUsageLoading] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState({ open: false, row: null, newLimit: "", scope: "this_month_only" });
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [topupDialog, setTopupDialog] = useState({ open: false, row: null, messages: 250, mode: "add_to_invoice" });
  const [topupSaving, setTopupSaving] = useState(false);

  const fetchSmsTestUsage = async () => {
    try {
      setSmsTestUsageLoading(true);
      const res = await axios.get(`${API}/pickup-safety/admin/test-sms-usage`);
      setSmsTestUsage(res.data || { month: "", rows: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Test SMS usage");
    } finally {
      setSmsTestUsageLoading(false);
    }
  };

  const fetchPickupSafetyUsage = async () => {
    try {
      setPickupSafetyUsageLoading(true);
      const res = await axios.get(`${API}/pickup-safety/admin/sms-usage`);
      setPickupSafetyUsage(res.data || { month: "", rows: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Pickup Safety usage");
    } finally {
      setPickupSafetyUsageLoading(false);
    }
  };

  const handleSendUpgradeEmail = async (row) => {
    if (!window.confirm(`Send upgrade email to ${row.organizationName}?`)) return;
    try {
      setSendingUpgradeEmailFor(row.organizationId);
      const res = await axios.post(`${API}/pickup-safety/admin/send-upgrade-email`, {
        organizationId: row.organizationId,
      });
      toast.success(`Email sent to ${res.data?.sentTo || row.organizationName}`);
      fetchPickupSafetyUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send upgrade email");
    } finally {
      setSendingUpgradeEmailFor(null);
    }
  };

  const fetchSmsAdminUsage = async () => {
    try {
      setSmsAdminUsageLoading(true);
      const res = await axios.get(`${API}/communications/admin/usage`);
      setSmsAdminUsage(res.data || { month: "", rows: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load SMS usage");
    } finally {
      setSmsAdminUsageLoading(false);
    }
  };

  const submitAdjustLimit = async () => {
    const row = adjustDialog.row;
    if (!row) return;
    const newLimit = parseInt(adjustDialog.newLimit, 10);
    if (isNaN(newLimit) || newLimit < 0) {
      toast.error("Enter a valid limit");
      return;
    }
    try {
      setAdjustSaving(true);
      const res = await axios.post(`${API}/communications/admin/adjust-limit`, {
        organizationId: row.organizationId,
        newLimit,
        scope: adjustDialog.scope,
      });
      toast.success(res.data?.message || "Limit adjusted");
      setAdjustDialog({ open: false, row: null, newLimit: "", scope: "this_month_only" });
      fetchSmsAdminUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to adjust limit");
    } finally {
      setAdjustSaving(false);
    }
  };

  const submitChargeTopup = async () => {
    const row = topupDialog.row;
    if (!row) return;
    try {
      setTopupSaving(true);
      const res = await axios.post(`${API}/communications/admin/charge-topup`, {
        organizationId: row.organizationId,
        messages: topupDialog.messages,
        mode: topupDialog.mode,
      });
      if (res.data?.mode === "square_link" && res.data?.payment_link_url) {
        toast.success("Square link emailed to customer.");
        window.open(res.data.payment_link_url, "_blank", "noopener,noreferrer");
      } else {
        toast.success(res.data?.message || "Top-up granted");
      }
      setTopupDialog({ open: false, row: null, messages: 250, mode: "add_to_invoice" });
      fetchSmsAdminUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process top-up");
    } finally {
      setTopupSaving(false);
    }
  };

  // Auto-refresh SMS Test Limits every 30s while the tab is active and visible
  useEffect(() => {
    if (activeTab !== "sms-test-limits") return;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchSmsTestUsage();
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Auto-refresh Pickup Safety Usage every 30s
  useEffect(() => {
    if (activeTab !== "pickup-safety-usage") return;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchPickupSafetyUsage();
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Auto-refresh SMS Usage & Limits every 30s
  useEffect(() => {
    if (activeTab !== "sms-usage-limits") return;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchSmsAdminUsage();
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const handleResetSmsTest = async (organizationId, organizationName) => {
    try {
      setResettingOrgId(organizationId);
      await axios.post(`${API}/pickup-safety/admin/test-sms-reset`, { organizationId });
      toast.success(`Reset Test SMS counter for ${organizationName}`);
      fetchSmsTestUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResettingOrgId(null);
    }
  };
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, type: null });
  const [deleting, setDeleting] = useState(false);
  const [subReport, setSubReport] = useState([]);
  const [subReportLoading, setSubReportLoading] = useState(false);
  const [revenueReport, setRevenueReport] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [testParents, setTestParents] = useState([]);
  const [testParentForm, setTestParentForm] = useState({ email: '', password: '', trialDays: 7, childName: '' });
  const [creatingTestParent, setCreatingTestParent] = useState(false);
  const [newTestParent, setNewTestParent] = useState(null);

  useEffect(() => {
    // Only allow primary owner access
    if (!isPrimaryOwner) {
      toast.error("Access denied - Primary owner account only");
      navigate("/");
      return;
    }
    fetchData();
  }, [isPrimaryOwner, navigate]);

  useEffect(() => {
    const term = searchTerm.toLowerCase().trim();
    
    if (term === "") {
      setFilteredCustomers(customers);
      setFilteredEmails(emails);
    } else {
      // Filter organizations
      const filteredOrgs = customers.filter(c => 
        c.organization?.name?.toLowerCase().includes(term) ||
        c.organization?.ownerEmail?.toLowerCase().includes(term) ||
        c.owner?.name?.toLowerCase().includes(term) ||
        c.organization?.status?.toLowerCase().includes(term) ||
        c.organization?.plan?.toLowerCase().includes(term)
      );
      setFilteredCustomers(filteredOrgs);
      
      // Filter emails
      const filteredE = emails.filter(e => 
        e.email?.toLowerCase().includes(term) ||
        e.name?.toLowerCase().includes(term) ||
        e.organizationName?.toLowerCase().includes(term) ||
        e.role?.toLowerCase().includes(term)
      );
      setFilteredEmails(filteredE);
    }
  }, [searchTerm, customers, emails]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersRes, emailsRes] = await Promise.all([
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/admin/all-emails`)
      ]);
      setCustomers(customersRes.data.customers || []);
      setFilteredCustomers(customersRes.data.customers || []);
      setEmails(emailsRes.data.emails || []);
      setFilteredEmails(emailsRes.data.emails || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied - Owner account only");
        navigate("/");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteDialog.item || deleteDialog.type !== 'org') return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/customers/${deleteDialog.item.organization.id}`);
      toast.success(`Deleted organization: ${deleteDialog.item.organization.name}`);
      setDeleteDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting organization:", error);
      toast.error(error.response?.data?.detail || "Failed to delete organization");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.item || deleteDialog.type !== 'user') return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/user/${deleteDialog.item.id}`);
      toast.success(response.data.message);
      setDeleteDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(parseISO(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-green-100 text-green-800",
      trial: "bg-yellow-100 text-yellow-800",
      expired: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800"
    };
    return (
      <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown"}
      </Badge>
    );
  };

  const getPlanBadge = (plan) => {
    const variants = {
      basic: "bg-blue-100 text-blue-800",
      deluxe: "bg-purple-100 text-purple-800",
      premium: "bg-amber-100 text-amber-800"
    };
    return (
      <Badge className={variants[plan] || "bg-gray-100 text-gray-800"}>
        {plan?.charAt(0).toUpperCase() + plan?.slice(1) || "Basic"}
      </Badge>
    );
  };

  const getRoleBadge = (role, isOwnerAccount) => {
    if (isOwnerAccount && role === "owner") {
      return <Badge className="bg-purple-100 text-purple-800">Main Owner</Badge>;
    }
    const variants = {
      owner: "bg-blue-100 text-blue-800",
      staff: "bg-green-100 text-green-800",
      admin: "bg-indigo-100 text-indigo-800"
    };
    return (
      <Badge className={variants[role] || "bg-gray-100 text-gray-800"}>
        {role?.charAt(0).toUpperCase() + role?.slice(1) || "Unknown"}
      </Badge>
    );
  };

  // Print Organizations Report
  const fetchTestParents = async () => {
    try {
      const res = await axios.get(`${API}/owner/test-parent-accounts`);
      setTestParents(res.data || []);
    } catch { /* silent */ }
  };

  const createTestParent = async () => {
    if (!testParentForm.email || !testParentForm.password) {
      toast.error("Email and password required");
      return;
    }
    setCreatingTestParent(true);
    try {
      const res = await axios.post(`${API}/owner/test-parent-accounts`, testParentForm);
      setNewTestParent(res.data);
      toast.success("Test parent account created");
      fetchTestParents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreatingTestParent(false);
    }
  };

  const deleteTestParent = async (id) => {
    if (!window.confirm("Delete this test parent account?")) return;
    try {
      await axios.delete(`${API}/owner/test-parent-accounts/${id}`);
      toast.success("Deleted");
      fetchTestParents();
      if (newTestParent?.email) setNewTestParent(null);
    } catch { toast.error("Failed to delete"); }
  };

  const fetchRevenueReport = async () => {
    setRevenueLoading(true);
    try {
      const res = await axios.get(`${API}/owner/revenue-settings-report`);
      setRevenueReport(res.data.report || []);
    } catch {
      toast.error("Failed to load revenue report");
    } finally {
      setRevenueLoading(false);
    }
  };

  const downloadRevenueCSV = () => {
    const headers = ["Org Name", "PS Subscribed", "PS Email Fee", "PS SMS Fee", "PS Email Active", "PS SMS Active", "PS Paying Parents", "PC Subscribed", "PC Fee/Child", "PC Activated Children", "PC Paying Parents"];
    const rows = revenueReport.map(r => [
      r.orgName,
      r.pickupSafety.subscribed ? "Yes" : "No",
      `$${r.pickupSafety.emailFee}`,
      `$${r.pickupSafety.smsFee}`,
      r.pickupSafety.emailActiveCount,
      r.pickupSafety.smsActiveCount,
      r.pickupSafety.payingParents,
      r.parentComm.subscribed ? "Yes" : "No",
      `$${r.parentComm.feePerChild}`,
      r.parentComm.activatedChildren,
      r.parentComm.payingParents,
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-settings-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchSubReport = async () => {
    setSubReportLoading(true);
    try {
      const res = await axios.get(`${API}/owner/parent-comm-report`);
      setSubReport(res.data.report || []);
    } catch {
      toast.error("Failed to load subscription report");
    } finally {
      setSubReportLoading(false);
    }
  };

  const downloadSubCSV = () => {
    const headers = ["Center Name", "Owner Email", "Plan", "Status", "Subscribed Date", "End Date", "Square ID"];
    const rows = subReport.map(r => [
      r.centerName, r.ownerEmail, r.billingCycle, r.status,
      r.subscribedAt ? new Date(r.subscribedAt).toLocaleDateString() : "—",
      r.endDate ? new Date(r.endDate).toLocaleDateString() : "—",
      r.squarePaymentId,
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `parent-comm-subscriptions-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Print Organizations Report
  const handlePrintOrganizations = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Organizations Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a365d; border-bottom: 2px solid #3182ce; padding-bottom: 10px; }
          .stats { display: flex; gap: 30px; margin-bottom: 20px; }
          .stat { background: #f7fafc; padding: 15px; border-radius: 8px; }
          .stat-label { font-size: 12px; color: #718096; }
          .stat-value { font-size: 24px; font-weight: bold; color: #2d3748; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #edf2f7; padding: 12px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #cbd5e0; }
          td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; }
          tr:nth-child(even) { background: #f7fafc; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }
          .badge-active { background: #c6f6d5; color: #276749; }
          .badge-trial { background: #fefcbf; color: #975a16; }
          .badge-expired { background: #fed7d7; color: #c53030; }
          .print-date { text-align: right; color: #718096; font-size: 12px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>Organizations Report</h1>
        <p class="print-date">Generated: ${new Date().toLocaleString()}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Total Organizations</div><div class="stat-value">${filteredCustomers.length}</div></div>
          <div class="stat"><div class="stat-label">Total Users</div><div class="stat-value">${emails.length}</div></div>
          <div class="stat"><div class="stat-label">Total Children</div><div class="stat-value">${customers.reduce((sum, c) => sum + (c.childrenCount || 0), 0)}</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Owner</th>
              <th>Email</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Children</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            ${filteredCustomers.map(c => {
              const statusClass = c.organization?.status === 'active' ? 'badge-active' : c.organization?.status === 'trial' ? 'badge-trial' : 'badge-expired';
              return `
                <tr>
                  <td>${c.organization?.name || 'N/A'}</td>
                  <td>${c.owner?.name || 'N/A'}</td>
                  <td>${c.organization?.ownerEmail || 'N/A'}</td>
                  <td><span class="badge ${statusClass}">${c.organization?.status || 'unknown'}</span></td>
                  <td>${c.organization?.plan || 'basic'}</td>
                  <td>${c.childrenCount || 0}</td>
                  <td>${formatDate(c.organization?.createdAt)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Print Emails Report
  const handlePrintEmails = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>All Users Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a365d; border-bottom: 2px solid #3182ce; padding-bottom: 10px; }
          .stats { display: flex; gap: 30px; margin-bottom: 20px; }
          .stat { background: #f7fafc; padding: 15px; border-radius: 8px; }
          .stat-label { font-size: 12px; color: #718096; }
          .stat-value { font-size: 24px; font-weight: bold; color: #2d3748; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #edf2f7; padding: 12px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #cbd5e0; }
          td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; }
          tr:nth-child(even) { background: #f7fafc; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }
          .badge-owner { background: #bee3f8; color: #2b6cb0; }
          .badge-staff { background: #c6f6d5; color: #276749; }
          .print-date { text-align: right; color: #718096; font-size: 12px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>All Users Report</h1>
        <p class="print-date">Generated: ${new Date().toLocaleString()}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Total Users</div><div class="stat-value">${filteredEmails.length}</div></div>
          <div class="stat"><div class="stat-label">Owners</div><div class="stat-value">${filteredEmails.filter(e => e.role === 'owner').length}</div></div>
          <div class="stat"><div class="stat-label">Staff</div><div class="stat-value">${filteredEmails.filter(e => e.role === 'staff').length}</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Role</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            ${filteredEmails.map(e => {
              const roleClass = e.role === 'owner' ? 'badge-owner' : 'badge-staff';
              return `
                <tr>
                  <td>${e.email || 'N/A'}</td>
                  <td>${e.name || 'N/A'}</td>
                  <td>${e.organizationName || 'N/A'}</td>
                  <td><span class="badge ${roleClass}">${e.role || 'unknown'}</span></td>
                  <td>${formatDate(e.createdAt)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <AppShell pageTitle="Admin Dashboard" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.customerPortal}
        title="Customer Portal"
        subtitle="Manage customer organizations and parent communications"
        altText="Parents using childcare portal"
      />
      
      <div data-testid="admin-customers-page" className="relative z-10">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-brand-accent/10 rounded-xl">
              <Building2 className="w-8 h-8 text-brand-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-ink-main">Admin Dashboard</h2>
              <p className="text-sm text-ink-muted">Manage customer organizations and users</p>
            </div>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-brand-primary text-brand-primary hover:bg-brand-primary/5 rounded-xl"
            disabled={loading}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white border border-line shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-brand-secondary/10 rounded-xl">
                  <Building2 className="w-6 h-6 text-brand-secondary" />
                </div>
                <div>
                  <p className="text-sm text-ink-muted">Organizations</p>
                  <p className="text-2xl font-bold text-ink-main">{customers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-line shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-success/10 rounded-xl">
                  <Users className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-sm text-ink-muted">Total Users</p>
                  <p className="text-2xl font-bold text-ink-main">{emails.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-line shadow-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-brand-highlight/10 rounded-xl">
                  <Baby className="w-6 h-6 text-brand-highlight" />
                </div>
                <div>
                  <p className="text-sm text-ink-muted">Total Children</p>
                  <p className="text-2xl font-bold text-ink-main">
                    {customers.reduce((sum, c) => sum + (c.childrenCount || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6 bg-white border border-line shadow-card">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink-muted" />
              <Input
                placeholder="Search by organization, email, name, status, or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-line"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-4 bg-white border border-line">
            <TabsTrigger value="organizations" className="data-[state=active]:bg-brand-primary data-[state=active]:text-white rounded-lg">
              <Building2 className="w-4 h-4 mr-2" />
              Organizations ({filteredCustomers.length})
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-brand-secondary data-[state=active]:text-white rounded-lg">
              <Mail className="w-4 h-4 mr-2" />
              All Emails ({filteredEmails.length})
            </TabsTrigger>
            <TabsTrigger value="ip-locations" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg">
              <MapPin className="w-4 h-4 mr-2" />
              IP Locations
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white rounded-lg">
              <Activity className="w-4 h-4 mr-2" />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="feedback-responses" className="data-[state=active]:bg-brand-highlight data-[state=active]:text-white rounded-lg">
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback Responses
            </TabsTrigger>
            <TabsTrigger value="parent-comm-subs" onClick={fetchSubReport} className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white rounded-lg">
              <FileText className="w-4 h-4 mr-2" />
              Parent Comm Subs
            </TabsTrigger>
            <TabsTrigger value="test-parents" onClick={fetchTestParents} className="data-[state=active]:bg-rose-500 data-[state=active]:text-white rounded-lg">
              <UserPlus className="w-4 h-4 mr-2" />
              Test Accounts
            </TabsTrigger>
            <TabsTrigger value="revenue-settings" onClick={fetchRevenueReport} className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white rounded-lg">
              <TrendingUp className="w-4 h-4 mr-2" />
              Revenue Settings
            </TabsTrigger>
            <TabsTrigger value="sms-test-limits" onClick={fetchSmsTestUsage} className="data-[state=active]:bg-amber-600 data-[state=active]:text-white rounded-lg" data-testid="tab-sms-test-limits">
              <Send className="w-4 h-4 mr-2" />
              SMS Test Limits
            </TabsTrigger>
            <TabsTrigger value="pickup-safety-usage" onClick={fetchPickupSafetyUsage} className="data-[state=active]:bg-rose-600 data-[state=active]:text-white rounded-lg" data-testid="tab-pickup-safety-usage">
              <Send className="w-4 h-4 mr-2" />
              Pickup Safety Usage
            </TabsTrigger>
            <TabsTrigger value="sms-usage-limits" onClick={fetchSmsAdminUsage} className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white rounded-lg" data-testid="tab-sms-usage-limits">
              <MessageSquare className="w-4 h-4 mr-2" />
              SMS Usage &amp; Limits
            </TabsTrigger>
          </TabsList>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-ink-main">
                  <Building2 className="w-6 h-6 text-brand-primary" />
                  Customer Organizations
                </CardTitle>
                <Button
                  onClick={handlePrintOrganizations}
                  variant="outline"
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  disabled={loading || filteredCustomers.length === 0}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Report
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-ink-muted">Loading organizations...</p>
                  </div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-brand-primary/30 mx-auto mb-4" />
                    <p className="text-ink-muted">
                      {searchTerm ? "No organizations match your search" : "No customer organizations found"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-surface-bg">
                          <TableHead className="font-semibold text-ink-main">Organization</TableHead>
                          <TableHead className="font-semibold text-ink-main">Owner</TableHead>
                          <TableHead className="font-semibold text-ink-main">Status</TableHead>
                          <TableHead className="font-semibold text-ink-main">Plan</TableHead>
                          <TableHead className="font-semibold text-ink-main text-center">Children</TableHead>
                          <TableHead className="font-semibold text-ink-main text-center">Sign-ins</TableHead>
                          <TableHead className="font-semibold text-ink-main">Created</TableHead>
                          <TableHead className="font-semibold text-ink-main">Trial/Renewal</TableHead>
                          <TableHead className="font-semibold text-ink-main text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCustomers.map((customer) => (
                          <TableRow key={customer.organization?.id} className="hover:bg-surface-bg/50">
                            <TableCell className="font-medium">
                              <div>
                                <p className="font-semibold text-ink-main">{customer.organization?.name}</p>
                                {customer.staffCount > 0 && (
                                  <p className="text-xs text-ink-muted">+{customer.staffCount} staff</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">{customer.organization?.ownerEmail}</p>
                                <p className="text-xs text-ink-muted">{customer.owner?.name || "N/A"}</p>
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(customer.organization?.status)}</TableCell>
                            <TableCell>{getPlanBadge(customer.organization?.plan)}</TableCell>
                            <TableCell className="text-center">
                              <span className="font-semibold text-brand-secondary">{customer.childrenCount || 0}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="font-semibold text-success">{customer.signinsCount || 0}</span>
                            </TableCell>
                            <TableCell>{formatDate(customer.organization?.createdAt)}</TableCell>
                            <TableCell>
                              {customer.organization?.status === 'trial' ? (
                                <div className="text-xs">
                                  <p className="text-brand-highlight font-medium">Trial Ends</p>
                                  <p className="text-ink-muted">{formatDate(customer.organization?.trialEndDate)}</p>
                                </div>
                              ) : customer.organization?.status === 'active' ? (
                                <div className="text-xs">
                                  <p className="text-success font-medium">Renews</p>
                                  <p className="text-ink-muted">{formatDate(customer.organization?.subscriptionEndDate) || 'N/A'}</p>
                                </div>
                              ) : (
                                <span className="text-ink-muted text-xs">N/A</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {customer.organization?.status === 'trial' && (
                                  <select
                                    className="text-xs border border-cyan-300 bg-white rounded px-1.5 py-0.5 hover:bg-cyan-50 cursor-pointer"
                                    defaultValue=""
                                    onChange={async (e) => {
                                      const day = parseInt(e.target.value, 10);
                                      e.target.value = "";
                                      if (!day) return;
                                      const label = day === 23 ? "Day 23 SMS" : `Day ${day} email`;
                                      if (!window.confirm(`Send ${label} to ${customer.organization?.name}?`)) return;
                                      try {
                                        const res = await axios.post(`${API}/admin/trial-reminders/send-now`, {
                                          organizationId: customer.organization.id,
                                          day,
                                        });
                                        toast.success(`${label} sent to ${res.data?.sentTo || customer.organization?.name}`);
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || `${label} failed`);
                                      }
                                    }}
                                    data-testid={`trial-reminder-${customer.organization?.id}`}
                                  >
                                    <option value="">Trial Reminder ▾</option>
                                    <option value="7">Day 7 — first message email</option>
                                    <option value="14">Day 14 — staff records email</option>
                                    <option value="23">Day 23 — SMS reminder</option>
                                    <option value="25">Day 25 — pick a plan email</option>
                                  </select>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-danger hover:text-danger hover:bg-danger/5"
                                  onClick={() => setDeleteDialog({ open: true, item: customer, type: 'org' })}
                                  data-testid={`delete-org-${customer.organization?.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-ink-main">
                  <Mail className="w-6 h-6 text-brand-secondary" />
                  All Registered Emails
                </CardTitle>
                <Button
                  onClick={handlePrintEmails}
                  variant="outline"
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  disabled={loading || filteredEmails.length === 0}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Report
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-ink-muted">Loading emails...</p>
                  </div>
                ) : filteredEmails.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 text-brand-secondary/30 mx-auto mb-4" />
                    <p className="text-ink-muted">
                      {searchTerm ? "No emails match your search" : "No registered emails found"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-surface-bg">
                          <TableHead className="font-semibold text-ink-main">Email</TableHead>
                          <TableHead className="font-semibold text-ink-main">Name</TableHead>
                          <TableHead className="font-semibold text-ink-main">Role</TableHead>
                          <TableHead className="font-semibold text-ink-main">Organization</TableHead>
                          <TableHead className="font-semibold text-ink-main">Registered</TableHead>
                          <TableHead className="font-semibold text-ink-main text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEmails.map((user) => (
                          <TableRow key={user.id} className="hover:bg-surface-bg/50">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-brand-secondary" />
                                {user.email}
                              </div>
                            </TableCell>
                            <TableCell>{user.name || "N/A"}</TableCell>
                            <TableCell>{getRoleBadge(user.role, user.isOwnerAccount)}</TableCell>
                            <TableCell>
                              <span className={user.isOwnerAccount ? "text-brand-accent font-medium" : ""}>
                                {user.organizationName}
                              </span>
                            </TableCell>
                            <TableCell>{formatDate(user.createdAt)}</TableCell>
                            <TableCell className="text-right">
                              {user.isOwnerAccount && user.role === "owner" ? (
                                <Badge className="bg-surface-bg text-ink-muted">Protected</Badge>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-danger hover:text-danger hover:bg-danger/5"
                                  onClick={() => setDeleteDialog({ open: true, item: user, type: 'user' })}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks">
            <WebhookActivityTab />
          </TabsContent>

          {/* IP Locations Tab */}
          <TabsContent value="ip-locations">
            <IPLocationsTab />
          </TabsContent>

          {/* Feedback Responses Tab */}
          <TabsContent value="feedback-responses">
            <FeedbackResponsesTab />
          </TabsContent>

          {/* Parent Communication Subscriptions Tab */}
          <TabsContent value="parent-comm-subs">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <CardTitle className="flex items-center gap-2 text-ink-main">
                  <FileText className="w-5 h-5 text-cyan-600" />
                  Parent Communication Subscriptions
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchSubReport} disabled={subReportLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${subReportLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadSubCSV} disabled={!subReport.length}>
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.print()} disabled={!subReport.length}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {subReportLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent" />
                  </div>
                ) : subReport.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No subscription records yet. Click Refresh to load.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Center Name</TableHead>
                          <TableHead>Owner Email</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Subscribed</TableHead>
                          <TableHead>Renews / Ends</TableHead>
                          <TableHead>Square ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subReport.map((row) => (
                          <TableRow key={row.orgId}>
                            <TableCell className="font-medium">{row.centerName}</TableCell>
                            <TableCell>{row.ownerEmail}</TableCell>
                            <TableCell className="capitalize">{row.billingCycle}</TableCell>
                            <TableCell>
                              <Badge className={
                                row.status === "active" ? "bg-emerald-100 text-emerald-700" :
                                row.status === "expired" ? "bg-red-100 text-red-700" :
                                "bg-slate-100 text-slate-600"
                              }>{row.status}</Badge>
                            </TableCell>
                            <TableCell>{row.subscribedAt ? new Date(row.subscribedAt).toLocaleDateString() : "—"}</TableCell>
                            <TableCell>{row.endDate ? new Date(row.endDate).toLocaleDateString() : "—"}</TableCell>
                            <TableCell className="text-xs font-mono text-slate-400 max-w-[120px] truncate">{row.squarePaymentId}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="mt-3 text-xs text-slate-400">{subReport.length} center{subReport.length !== 1 ? "s" : ""} total</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Accounts Tab — Centers + Parents */}
          <TabsContent value="test-parents">
            <div className="space-y-6">

              {/* Create Test Center Account */}
              <Card className="bg-white border border-line shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-700">
                    <Building2 className="w-5 h-5" />
                    Create Test Center Account
                  </CardTitle>
                  <CardDescription>Create a temporary childcare center account for testing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Organization Name *</label>
                      <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Test Childcare Center"
                        value={testParentForm.orgName || ''} onChange={e => setTestParentForm(p => ({...p, orgName: e.target.value}))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Email *</label>
                      <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="center@test.com"
                        value={testParentForm.centerEmail || ''} onChange={e => setTestParentForm(p => ({...p, centerEmail: e.target.value}))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Password *</label>
                      <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Enter password"
                        value={testParentForm.centerPassword || ''} onChange={e => setTestParentForm(p => ({...p, centerPassword: e.target.value}))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Expires (days)</label>
                      <input type="number" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" defaultValue={14}
                        value={testParentForm.centerDays || 14} onChange={e => setTestParentForm(p => ({...p, centerDays: e.target.value}))} />
                    </div>
                  </div>
                  <button onClick={async () => {
                    if (!testParentForm.orgName || !testParentForm.centerEmail || !testParentForm.centerPassword) { toast.error("Fill in all fields"); return; }
                    try {
                      await axios.post(`${API}/owner/test-accounts`, {
                        organizationName: testParentForm.orgName,
                        email: testParentForm.centerEmail,
                        password: testParentForm.centerPassword,
                        tier: 'enterprise',
                        expirationDays: parseInt(testParentForm.centerDays || 14)
                      });
                      toast.success("Test center account created");
                      setTestParentForm(p => ({...p, orgName:'', centerEmail:'', centerPassword:'', centerDays:14}));
                    } catch(err) { toast.error(err.response?.data?.detail || "Failed"); }
                  }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-lg text-sm transition">
                    Create Test Center Account
                  </button>
                </CardContent>
              </Card>

              {/* Create Test Trial Center — with trial-day position picker */}
              <Card className="bg-white border border-line shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-cyan-700">
                    <FlaskConical className="w-5 h-5" />
                    Create Test Trial Center
                  </CardTitle>
                  <CardDescription>
                    Creates a center already in <b>trial</b> status, positioned on any day of the 30-day trial so you can test the reminder flow (Day 7/14/23 SMS/25 emails) immediately.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Organization Name *</label>
                      <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Test Trial Center"
                        value={testParentForm.trialOrgName || ''} onChange={e => setTestParentForm(p => ({...p, trialOrgName: e.target.value}))}
                        data-testid="trial-test-org-name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Email *</label>
                      <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="trial-test@example.com"
                        value={testParentForm.trialEmail || ''} onChange={e => setTestParentForm(p => ({...p, trialEmail: e.target.value}))}
                        data-testid="trial-test-email" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Password *</label>
                      <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Enter password"
                        value={testParentForm.trialPassword || ''} onChange={e => setTestParentForm(p => ({...p, trialPassword: e.target.value}))}
                        data-testid="trial-test-password" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Mobile Phone (for Day 23 SMS)</label>
                      <input type="tel" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="(555) 123-4567"
                        value={testParentForm.trialPhone || ''} onChange={e => setTestParentForm(p => ({...p, trialPhone: e.target.value}))}
                        data-testid="trial-test-phone" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-2">Start trial on which day?</label>
                    <div className="flex flex-wrap gap-2">
                      {[1, 7, 14, 23, 25].map(d => (
                        <label key={d} className={`px-3 py-1.5 rounded-lg border-2 text-xs cursor-pointer ${(testParentForm.trialDayPosition || 1) === d ? 'border-cyan-600 bg-cyan-50 font-semibold' : 'border-slate-300 bg-white'}`}>
                          <input
                            type="radio"
                            name="trialDayPos"
                            className="sr-only"
                            checked={(testParentForm.trialDayPosition || 1) === d}
                            onChange={() => setTestParentForm(p => ({...p, trialDayPosition: d}))}
                            data-testid={`trial-day-${d}`}
                          />
                          Day {d}
                          {d === 23 ? ' (SMS)' : d === 1 ? ' (fresh)' : ' (email)'}
                        </label>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">
                      Pick the day to test — e.g. "Day 7" creates an account already 7 days into trial so the Day 7 email will fire on the next cron run (or when you manually trigger it).
                    </p>
                  </div>
                  <label className="flex items-start gap-2 text-xs cursor-pointer border border-slate-200 bg-slate-50 rounded-lg p-2" data-testid="trial-seed-toggle">
                    <input
                      type="checkbox"
                      checked={!!testParentForm.seedSampleData}
                      onChange={(e) => setTestParentForm(p => ({...p, seedSampleData: e.target.checked}))}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-semibold text-slate-700">Seed with sample data</span>
                      <span className="block text-slate-500">
                        Pre-creates <b>5 children</b>, <b>2 staff records</b>, and <b>3 compliance documents</b> (one active, one expiring soon, one expired) so dashboards show realistic visuals immediately. All tagged as test data — delete cleans them all up in one click.
                      </span>
                    </span>
                  </label>
                  <button onClick={async () => {
                    if (!testParentForm.trialOrgName || !testParentForm.trialEmail || !testParentForm.trialPassword) {
                      toast.error("Fill in all required fields"); return;
                    }
                    try {
                      await axios.post(`${API}/owner/test-accounts`, {
                        organizationName: testParentForm.trialOrgName,
                        email: testParentForm.trialEmail,
                        password: testParentForm.trialPassword,
                        phone: testParentForm.trialPhone || null,
                        tier: 'trial',
                        trialDayPosition: testParentForm.trialDayPosition || 1,
                        seedSampleData: !!testParentForm.seedSampleData,
                      });
                      const seedMsg = testParentForm.seedSampleData ? ' (with sample data)' : '';
                      toast.success(`Test trial center created on Day ${testParentForm.trialDayPosition || 1}${seedMsg}`);
                      setTestParentForm(p => ({...p, trialOrgName:'', trialEmail:'', trialPassword:'', trialPhone:'', trialDayPosition:1, seedSampleData:false}));
                      fetchTestParents();
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                  }} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-2 rounded-lg text-sm transition"
                    data-testid="create-test-trial-center-btn">
                    Create Test Trial Center
                  </button>
                </CardContent>
              </Card>

              {/* Create Test Parent Account */}
              <Card className="bg-white border border-line shadow-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-rose-700">
                        <UserPlus className="w-5 h-5" />
                        Create Test Parent Account
                      </CardTitle>
                      <CardDescription>Creates a parent login connected to your childcare center — no real parent involved</CardDescription>
                    </div>
                    <button
                      onClick={() => setTestParentForm(p => ({ ...p, email: user?.email?.replace('@', '+parent@') || '', password: 'TestParent123', trialDays: 7 }))}
                      className="text-xs font-semibold text-rose-600 border border-rose-200 px-3 py-1.5 rounded-lg hover:bg-rose-50 transition"
                    >
                      Create for me
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Email *</label>
                      <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="testparent@example.com"
                        value={testParentForm.email} onChange={e => setTestParentForm(p => ({...p, email: e.target.value}))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Password *</label>
                      <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Choose a password"
                        value={testParentForm.password} onChange={e => setTestParentForm(p => ({...p, password: e.target.value}))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Trial Days</label>
                      <input type="number" min="1" max="90" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        value={testParentForm.trialDays} onChange={e => setTestParentForm(p => ({...p, trialDays: parseInt(e.target.value)}))} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">Child Name (optional)</label>
                      <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Search child by name..."
                        value={testParentForm.childName} onChange={e => setTestParentForm(p => ({...p, childName: e.target.value}))} />
                    </div>
                  </div>
                  <button onClick={createTestParent} disabled={creatingTestParent}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2 rounded-lg text-sm transition disabled:opacity-50">
                    {creatingTestParent ? "Creating..." : "Create Test Parent Account"}
                  </button>

                  {/* Result */}
                  {newTestParent && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2 mt-2">
                      <p className="text-sm font-bold text-emerald-800">Test Parent Account Ready</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-slate-500">Email:</span> <span className="font-semibold">{newTestParent.email}</span></div>
                        <div><span className="text-slate-500">Password:</span> <span className="font-semibold">{newTestParent.password}</span></div>
                        <div><span className="text-slate-500">Child:</span> <span className="font-semibold">{newTestParent.childLinked}</span></div>
                        <div><span className="text-slate-500">Trial:</span> <span className="font-semibold">{newTestParent.trialDays} days</span></div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <a href={newTestParent.loginUrl} target="_blank" rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2 rounded-lg transition">
                          <ExternalLink className="w-3.5 h-3.5" /> Open Parent Login
                        </a>
                        <button onClick={() => { navigator.clipboard.writeText(newTestParent.loginUrl); toast.success("URL copied"); }}
                          className="flex items-center gap-1.5 border border-slate-300 text-slate-600 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-slate-50 transition">
                          <Copy className="w-3.5 h-3.5" /> Copy URL
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Existing test parents */}
                  {testParents.length > 0 && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Existing Test Parents</p>
                      {testParents.map(p => (
                        <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{p.email}</p>
                            <p className="text-xs text-slate-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</p>
                          </div>
                          <button onClick={() => deleteTestParent(p.id)} className="text-rose-500 hover:text-rose-700 text-xs font-semibold flex items-center gap-1">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bulk Delete All Test Accounts */}
              <Card className="bg-rose-50 border-2 border-rose-200 shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-rose-700">
                    <Trash2 className="w-5 h-5" />
                    Bulk Cleanup
                  </CardTitle>
                  <CardDescription className="text-rose-600">
                    Delete <b>every</b> test account and all their associated data (children, staff, compliance docs, attendance, etc.) in one shot. Use this at the end of a sprint or before a demo. Real customer accounts are NOT affected.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Delete ALL test accounts and their seeded data?\n\nThis cannot be undone. Real customer accounts are not affected.")) return;
                      if (!window.confirm("Really sure? This wipes every test center + test parent + their children, staff, and compliance docs.")) return;
                      try {
                        const res = await axios.delete(`${API}/owner/test-accounts`);
                        const t = res.data?.totals || {};
                        toast.success(`Wiped ${res.data?.totals?.accounts_removed ?? 0} test accounts. Children:${t.children||0} Staff:${t.staff_records||0} Docs:${t.compliance_documents||0}`);
                        fetchTestParents();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Bulk delete failed");
                      }
                    }}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2.5 rounded-lg text-sm transition flex items-center justify-center gap-2"
                    data-testid="bulk-delete-test-accounts-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete All Test Accounts + Their Data
                  </button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="revenue-settings">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-ink-main">
                    <TrendingUp className="w-5 h-5 text-emerald-700" />
                    Revenue Settings Report
                  </CardTitle>
                  <CardDescription>What each org is charging for Pickup Safety Alerts & Parent Communication</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchRevenueReport} disabled={revenueLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${revenueLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadRevenueCSV} disabled={!revenueReport.length}>
                    <Download className="w-4 h-4 mr-2" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.print()} disabled={!revenueReport.length}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {revenueLoading ? (
                  <div className="flex justify-center py-10"><div className="h-7 w-7 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" /></div>
                ) : revenueReport.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Click Refresh to load the report.</p>
                  </div>
                ) : (
                  <table className="w-full text-sm min-w-[900px]">
                    <thead>
                      <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <th className="pb-3 pr-3">Organization</th>
                        <th className="pb-3 pr-3 text-center" colSpan={5}>Pickup Safety Alerts</th>
                        <th className="pb-3 pr-3 text-center" colSpan={4}>Parent Communication</th>
                      </tr>
                      <tr className="border-b text-left text-xs text-slate-400">
                        <th className="pb-2 pr-3"></th>
                        <th className="pb-2 pr-2">Sub</th>
                        <th className="pb-2 pr-2">Email Fee</th>
                        <th className="pb-2 pr-2">SMS Fee</th>
                        <th className="pb-2 pr-2">Email Active</th>
                        <th className="pb-2 pr-3">Paying</th>
                        <th className="pb-2 pr-2">Sub</th>
                        <th className="pb-2 pr-2">Fee/Child</th>
                        <th className="pb-2 pr-2">Activated</th>
                        <th className="pb-2">Paying</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {revenueReport.map(r => (
                        <tr key={r.orgId} className="hover:bg-slate-50">
                          <td className="py-2.5 pr-3 font-semibold text-slate-800">{r.orgName}</td>
                          {/* Pickup Safety */}
                          <td className="py-2.5 pr-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.pickupSafety.subscribed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                              {r.pickupSafety.subscribed ? "Active" : "No"}
                            </span>
                          </td>
                          <td className="py-2.5 pr-2 text-slate-600">${r.pickupSafety.emailFee.toFixed(2)}</td>
                          <td className="py-2.5 pr-2 text-slate-600">${r.pickupSafety.smsFee.toFixed(2)}</td>
                          <td className="py-2.5 pr-2 text-center text-slate-600">{r.pickupSafety.emailActiveCount}</td>
                          <td className="py-2.5 pr-3 text-center font-semibold text-emerald-700">{r.pickupSafety.payingParents}</td>
                          {/* Parent Comm */}
                          <td className="py-2.5 pr-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.parentComm.subscribed ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 text-slate-500"}`}>
                              {r.parentComm.subscribed ? "Active" : "No"}
                            </span>
                          </td>
                          <td className="py-2.5 pr-2 text-slate-600">${r.parentComm.feePerChild.toFixed(2)}</td>
                          <td className="py-2.5 pr-2 text-center text-slate-600">{r.parentComm.activatedChildren}</td>
                          <td className="py-2.5 text-center font-semibold text-cyan-700">{r.parentComm.payingParents}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Test Limits Tab */}
          <TabsContent value="sms-test-limits">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-ink-main">
                    <Send className="w-5 h-5 text-amber-600" />
                    Pickup SMS Test Limits — {smsTestUsage.month || "current month"}
                  </CardTitle>
                  <CardDescription>
                    Each customer gets 5 free Send-Test-SMS clicks per calendar month (auto-resets on the 1st).
                    You can manually reset a customer's count below.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSmsTestUsage}
                  disabled={smsTestUsageLoading}
                  data-testid="refresh-sms-test-usage-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${smsTestUsageLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {smsTestUsage.rows.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">
                    {smsTestUsageLoading ? "Loading..." : "No customer organizations found."}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="w-32 text-center">Used / Limit</TableHead>
                        <TableHead className="w-44">Last Test</TableHead>
                        <TableHead className="w-28">Status</TableHead>
                        <TableHead className="w-28 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {smsTestUsage.rows.map((row) => {
                        const overLimit = row.testSmsCount >= row.testSmsMonthlyLimit;
                        return (
                          <TableRow key={row.organizationId} data-testid={`sms-test-row-${row.organizationId}`}>
                            <TableCell className="font-medium">{row.organizationName}</TableCell>
                            <TableCell className="text-center">
                              <span className={overLimit ? "text-red-600 font-semibold" : "text-gray-700"}>
                                {row.testSmsCount} / {row.testSmsMonthlyLimit}
                              </span>
                            </TableCell>
                            <TableCell>
                              {row.lastTestSmsAt
                                ? new Date(row.lastTestSmsAt).toLocaleString()
                                : <span className="text-gray-400">Never</span>}
                            </TableCell>
                            <TableCell>
                              {row.lastTestSmsStatus === "delivered"
                                ? <span className="text-green-600 text-sm">✓ Delivered</span>
                                : row.lastTestSmsStatus === "failed"
                                  ? <span className="text-red-600 text-sm">✗ Failed</span>
                                  : <span className="text-gray-400 text-sm">—</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={resettingOrgId === row.organizationId || row.testSmsCount === 0}
                                onClick={() => handleResetSmsTest(row.organizationId, row.organizationName)}
                                data-testid={`reset-sms-test-${row.organizationId}`}
                              >
                                <RotateCcw className={`w-4 h-4 mr-1 ${resettingOrgId === row.organizationId ? 'animate-spin' : ''}`} />
                                Reset
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pickup Safety Usage Tab — actual sends, owner-only, no customer-visible counter */}
          <TabsContent value="pickup-safety-usage">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-ink-main">
                    <Send className="w-5 h-5 text-rose-600" />
                    Pickup Safety Usage — {pickupSafetyUsage.month || "current month"}
                  </CardTitle>
                  <CardDescription>
                    Real Pickup Safety alert SMS sent to parents this calendar month. Counter is uncapped — alerts always go through.
                    A courtesy email is auto-sent to the customer (and you) once a center crosses 500/month, then once per month thereafter.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPickupSafetyUsage}
                  disabled={pickupSafetyUsageLoading}
                  data-testid="refresh-pickup-safety-usage-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${pickupSafetyUsageLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {pickupSafetyUsage.rows.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">
                    {pickupSafetyUsageLoading ? "Loading..." : "No customer organizations found."}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="w-32 text-center">Sent This Month</TableHead>
                        <TableHead className="w-44">Last Sent</TableHead>
                        <TableHead className="w-44">Status</TableHead>
                        <TableHead className="w-56">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pickupSafetyUsage.rows.map((row) => {
                        let statusBadge;
                        if (row.status === "alerted") {
                          statusBadge = (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-xs font-semibold">
                              ⚠️ Over 500 — Alerted
                            </span>
                          );
                        } else if (row.status === "over") {
                          statusBadge = (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-xs font-semibold">
                              Over 500
                            </span>
                          );
                        } else if (row.status === "heads_up") {
                          statusBadge = (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
                              Heads-up (300+)
                            </span>
                          );
                        } else {
                          statusBadge = (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">
                              Normal
                            </span>
                          );
                        }
                        return (
                          <TableRow key={row.organizationId} data-testid={`pickup-safety-row-${row.organizationId}`}>
                            <TableCell className="font-medium">{row.organizationName}</TableCell>
                            <TableCell className="text-center">
                              <span className={row.smsCount >= 500 ? "text-rose-600 font-semibold" : "text-gray-700"}>
                                {row.smsCount}
                              </span>
                            </TableCell>
                            <TableCell>
                              {row.lastSentAt
                                ? new Date(row.lastSentAt).toLocaleString()
                                : <span className="text-gray-400">Never</span>}
                            </TableCell>
                            <TableCell>{statusBadge}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSendUpgradeEmail(row)}
                                  disabled={sendingUpgradeEmailFor === row.organizationId}
                                  data-testid={`upgrade-email-btn-${row.organizationId}`}
                                  className="text-xs"
                                >
                                  <Mail className="w-3 h-3 mr-1" />
                                  {sendingUpgradeEmailFor === row.organizationId ? "Sending..." : "Email About Upgrading"}
                                </Button>
                                {row.lastUpgradeEmailSentAt && (
                                  <span className="text-[10px] text-gray-500">
                                    Last sent: {new Date(row.lastUpgradeEmailSentAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Usage & Limits Tab — parent-comm cap admin (Stage 8) */}
          <TabsContent value="sms-usage-limits">
            <Card className="bg-white border border-line shadow-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-ink-main">
                    <MessageSquare className="w-5 h-5 text-cyan-600" />
                    SMS Usage &amp; Limits — {smsAdminUsage.month || "current month"}
                  </CardTitle>
                  <CardDescription>
                    Parent communication SMS usage per customer. Adjust caps for the current month
                    or permanently. Generate Square top-up payment links or grant credits without charge.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSmsAdminUsage}
                  disabled={smsAdminUsageLoading}
                  data-testid="refresh-sms-usage-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${smsAdminUsageLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {smsAdminUsage.rows.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">
                    {smsAdminUsageLoading ? "Loading..." : "No customer organizations found."}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="w-24">Plan</TableHead>
                        <TableHead className="w-40 text-center">Used / Allowance</TableHead>
                        <TableHead className="w-24 text-center">Top-ups</TableHead>
                        <TableHead className="w-32">Status</TableHead>
                        <TableHead className="w-64">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {smsAdminUsage.rows.map((row) => {
                        const statusColor = {
                          over: "bg-rose-100 text-rose-800",
                          warning: "bg-orange-100 text-orange-800",
                          heads_up: "bg-amber-100 text-amber-800",
                          normal: "bg-emerald-100 text-emerald-800",
                        }[row.statusBadge] || "bg-gray-100 text-gray-800";
                        const statusLabel = {
                          over: "Over Cap",
                          warning: "80–100%",
                          heads_up: "60–80%",
                          normal: "Normal",
                        }[row.statusBadge] || row.statusBadge;
                        return (
                          <TableRow key={row.organizationId} data-testid={`sms-admin-row-${row.organizationId}`}>
                            <TableCell className="font-medium">{row.organizationName}</TableCell>
                            <TableCell><Badge variant="outline" className="text-xs">{row.plan || "—"}</Badge></TableCell>
                            <TableCell className="text-center">
                              <span className={row.pct >= 100 ? "text-rose-600 font-semibold" : "text-gray-700"}>
                                {row.monthlyUsed} / {row.totalAllowance}
                              </span>
                              <div className="text-[10px] text-gray-500">{row.pct}%</div>
                            </TableCell>
                            <TableCell className="text-center text-sm">{row.topupCredits}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor}`}>
                                {statusLabel}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setAdjustDialog({ open: true, row, newLimit: String(row.monthlyLimit), scope: "this_month_only" })}
                                  data-testid={`adjust-limit-btn-${row.organizationId}`}
                                  className="text-xs"
                                >
                                  Adjust Limit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setTopupDialog({ open: true, row, messages: 250, mode: "add_to_invoice" })}
                                  data-testid={`charge-topup-btn-${row.organizationId}`}
                                  className="text-xs"
                                >
                                  Charge Top-up
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Adjust Limit Dialog (Stage 8) */}
        <AlertDialog open={adjustDialog.open} onOpenChange={(open) => !adjustSaving && setAdjustDialog({ ...adjustDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Adjust SMS Limit — {adjustDialog.row?.organizationName}</AlertDialogTitle>
              <AlertDialogDescription>
                Currently {adjustDialog.row?.monthlyLimit || 0} messages/month
                {adjustDialog.row?.topupCredits ? ` (+ ${adjustDialog.row.topupCredits} top-up credits)` : ""}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-semibold text-gray-700">New Limit</label>
                <Input
                  type="number"
                  value={adjustDialog.newLimit}
                  onChange={(e) => setAdjustDialog({ ...adjustDialog, newLimit: e.target.value })}
                  data-testid="adjust-limit-input"
                  className="mt-1"
                  min="0"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Scope</label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      checked={adjustDialog.scope === "this_month_only"}
                      onChange={() => setAdjustDialog({ ...adjustDialog, scope: "this_month_only" })}
                      data-testid="adjust-scope-this-month"
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-semibold">This month only</span> — adds headroom via top-up credits, baseline reverts next month.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      checked={adjustDialog.scope === "permanent"}
                      onChange={() => setAdjustDialog({ ...adjustDialog, scope: "permanent" })}
                      data-testid="adjust-scope-permanent"
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-semibold">Permanent</span> — overwrites the customer's monthly cap going forward.
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={adjustSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={submitAdjustLimit} disabled={adjustSaving} data-testid="adjust-save-btn">
                {adjustSaving ? "Saving..." : "Save"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Charge Top-up Dialog (Stage 8) */}
        <AlertDialog open={topupDialog.open} onOpenChange={(open) => !topupSaving && setTopupDialog({ ...topupDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Top-up SMS Credits — {topupDialog.row?.organizationName}</AlertDialogTitle>
              <AlertDialogDescription>
                Generate a Square payment link OR grant credits as a goodwill gesture (no charge).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Bundle</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setTopupDialog({ ...topupDialog, messages: 250 })}
                    data-testid="topup-bundle-250"
                    className={`rounded-lg border-2 px-3 py-2 text-sm ${topupDialog.messages === 250 ? "border-cyan-600 bg-cyan-50" : "border-gray-300 bg-white"}`}
                  >
                    <div className="font-semibold">250 messages</div>
                    <div className="text-xs text-gray-600">$9.99</div>
                  </button>
                  <button
                    onClick={() => setTopupDialog({ ...topupDialog, messages: 500 })}
                    data-testid="topup-bundle-500"
                    className={`rounded-lg border-2 px-3 py-2 text-sm ${topupDialog.messages === 500 ? "border-cyan-600 bg-cyan-50" : "border-gray-300 bg-white"}`}
                  >
                    <div className="font-semibold">500 messages</div>
                    <div className="text-xs text-gray-600">$17.99</div>
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Mode</label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      checked={topupDialog.mode === "square_link"}
                      onChange={() => setTopupDialog({ ...topupDialog, mode: "square_link" })}
                      data-testid="topup-mode-square"
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-semibold">Send Square link</span> — emails the customer a payment link. Credits added on payment.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      checked={topupDialog.mode === "add_to_invoice"}
                      onChange={() => setTopupDialog({ ...topupDialog, mode: "add_to_invoice" })}
                      data-testid="topup-mode-grant"
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-semibold">Grant credits (no charge)</span> — adds credits immediately as a goodwill gesture or refund.
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={topupSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={submitChargeTopup} disabled={topupSaving} data-testid="topup-save-btn">
                {topupSaving ? "Processing..." : (topupDialog.mode === "square_link" ? "Send Link" : "Grant Credits")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-danger">
                {deleteDialog.type === 'org' ? 'Delete Organization' : 'Delete User'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteDialog.type === 'org' ? (
                  <>
                    Are you sure you want to delete this organization and all its data?
                    <div className="mt-4 p-4 bg-danger/5 rounded-lg border border-danger/20">
                      <p className="font-semibold text-ink-main">{deleteDialog.item?.organization?.name}</p>
                      <p className="text-sm text-ink-muted">Owner: {deleteDialog.item?.organization?.ownerEmail}</p>
                      <p className="text-sm text-ink-muted">Children: {deleteDialog.item?.childrenCount || 0}</p>
                      <p className="text-sm text-ink-muted">Sign-ins: {deleteDialog.item?.signinsCount || 0}</p>
                    </div>
                  </>
                ) : (
                  <>
                    Are you sure you want to delete this user?
                    <div className="mt-4 p-4 bg-danger/5 rounded-lg border border-danger/20">
                      <p className="font-semibold text-ink-main">{deleteDialog.item?.email}</p>
                      <p className="text-sm text-ink-muted">{deleteDialog.item?.name} ({deleteDialog.item?.role})</p>
                      <p className="text-sm text-ink-muted">Organization: {deleteDialog.item?.organizationName}</p>
                    </div>
                  </>
                )}
                <p className="mt-4 text-sm text-danger font-medium">
                  This action cannot be undone.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteDialog.type === 'org' ? handleDeleteOrg : handleDeleteUser}
                disabled={deleting}
                className="bg-danger hover:bg-danger/90"
              >
                {deleting ? "Deleting..." : `Delete ${deleteDialog.type === 'org' ? 'Organization' : 'User'}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
};

export default AdminCustomersPage;
